<template>
	<section>
		<h1>Statistiken</h1>

		<el-form :label-position="'top'">
			<el-form-item label="Jahr" prop="year" class="el-col el-col-24 el-col-sm-8 el-col-xs-24">
				<el-select v-model="year" @change="loadAll()">
					<el-option v-for="item in yearOptions" :key="item.value" :label="item.label" :value="item.value" />
				</el-select>
			</el-form-item>
		</el-form>
	</section>

	<div class="bg--white" v-if="!loading">
		<section id="searchresults">
			<h2>Entscheidungen {{ year }}</h2>

			<el-row class="statistik__totalcounts">
				<TheBox :title="`${allComplaints.length}`">
					<span>Beschwerden gesamt</span>
				</TheBox>
				<TheBox :title="`${allDecisions.length}`">
					<span>Entscheidungen gesamt</span>
				</TheBox>
			</el-row>

			<CategoryBarChart :categoryData="decisionCategories" :totalCount="allDecisions.length" :totalCountType="'Entscheidungen'" />

			<h3>Angegebener Grund</h3>
			<TheTable :tableData="complaintCategories" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Grund der Beschwerde" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>

			<h3>Medium</h3>
			<TheTable :tableData="decisionMediums" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Betroffenes Medium" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>

			<h3>Branche</h3>
			<TheTable :tableData="casesSectors" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Betroffene Branche" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import TheBox from '@/components/TheBox.vue';
import TheTable from '@/components/TheTable.vue';
import CategoryBarChart from '@/components/charts/CategoryBarChart.vue';

import { searchPublicCasesAndComplaints, searchPublicComplaints } from '@/requests/searches.js';
import { CaseAndComplaintPublicResponse, ComplaintPublicResponse, ComplaintState } from '@/types/types';

import moment from 'moment';

interface YearOption {
	label: string;
	value: number;
}

interface StatistikPublicData {
	year: Number;
	loading: boolean;
	tablePage: number;
	tablePageComplaints: number;
	itemsPerPage: number;
	data: CaseAndComplaintPublicResponse[];
	dataComplaints: ComplaintPublicResponse[];
	totalCount: number;
	totalCountComplaints: number;
	errorFetchingData: boolean;
	errorFetchingComplaints: boolean;
}

export default defineComponent({
	name: 'StatistikPublic',
	data(): StatistikPublicData {
		return {
			year: new Date().getFullYear(),
			loading: true,
			tablePage: 0,
			tablePageComplaints: 0,
			data: [],
			dataComplaints: [],
			totalCount: 0,
			itemsPerPage: 20,
			totalCountComplaints: 0,
			errorFetchingData: false,
			errorFetchingComplaints: false,
		};
	},
	components: {
		TheBox,
		TheTable,
		CategoryBarChart,
	},
	computed: {
		yearOptions() {
			const currentYear = new Date().getFullYear();
			const startYear = 2024;

			let options: YearOption[] = [];
			for (let i = startYear; i <= currentYear; i++) {
				options.push({ label: String(i), value: i });
			}

			return options;
		},
		allDecisions() {
			if (this.data.length === 0) return [];

			let filtered = this.data.filter((item) => {
				const publishedYear = Number(this.$formatDate(item.publishedDate, 'YYYY'));
				const published24HoursAgo = moment().diff(moment(item.publishedDate), 'hours') >= 24;
				const decided24HoursAgo = moment().diff(moment(item.decidedDate), 'hours') >= 24;
				if (publishedYear === this.year && published24HoursAgo && decided24HoursAgo) return true;
				else return false;
			});

			return filtered;
		},
		allComplaints() {
			if (this.dataComplaints.length === 0) return [];

			let filtered = this.dataComplaints.filter((item) => {
				const publishedYear = Number(this.$formatDate(item.publishedDate, 'YYYY'));
				const published24HoursAgo = moment().diff(moment(item.publishedDate), 'hours') >= 24;
				const decided24HoursAgo = moment().diff(moment(item.decidedDate), 'hours') >= 24;
				if (publishedYear === this.year && published24HoursAgo && decided24HoursAgo) return true;
				else return false;
			});

			return filtered;
		},
		decisionCategories() {
			if (this.allDecisions.length === 0) return [];

			return this.allDecisions.reduce((acc, item) => {
				let name = '';
				if (item.state) {
					name = this.$getStateFullLabel('case', item.state, false);
				} else if (item.caseState) {
					name = this.$getStateFullLabel('case', item.caseState, false);
				} else if (!item.isCase) {
					name = this.$getStateFullLabel('complaint', ComplaintState.NotResponsible, false);
				}

				// check if the state already exists
				const index = acc.findIndex((category) => category.category.name === name);

				if (index > -1) {
					acc[index].count++;
				} else {
					acc.push({
						category: { number: '', name: name },
						count: 1,
					});
				}

				return acc;
			}, [] as Array<{ category: { number: string; name: string }; count: number }>);
		},
		decisionMediums() {
			if (this.allDecisions.length === 0) return [];

			const mediumCounts = this.allDecisions
				.reduce((acc, item) => {
					item.mediums.forEach((med) => {
						const mediumName = med.name;
						const existingMedium = acc.find((m) => m.name === mediumName);

						if (existingMedium) {
							existingMedium.count++;
						} else {
							acc.push({
								name: mediumName,
								count: 1,
								percent: '',
							});
						}
					});

					return acc;
				}, [] as Array<{ name: string; count: number; percent: string }>)
				.map((med) => {
					let percent = Number(((med.count / this.allComplaints.length) * 100).toFixed(2));

					return {
						...med,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			mediumCounts.push({ name: 'Summe (Entscheidungen)', count: mediumCounts.reduce((acc, med) => acc + med.count, 0), percent: '100%' });

			return mediumCounts;
		},
		complaintCategories() {
			if (this.allComplaints.length === 0) return [];

			let categoryCounts = this.allComplaints
				.reduce((acc, item) => {
					const categoryName = `${item.category.number} ${item.category.name}`;
					const existingCategory = acc.find((c) => c.name === categoryName);

					if (existingCategory) {
						existingCategory.count++;
					} else {
						acc.push({
							name: categoryName,
							count: 1,
							percent: '',
						});
					}

					return acc;
				}, [] as Array<{ name: string; count: number; percent: string }>)
				.map((category) => {
					let percent = Number(((category.count / this.allComplaints.length) * 100).toFixed(2));
					return {
						...category,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			categoryCounts.push({ name: 'Summe (Beschwerden)', count: categoryCounts.reduce((acc, cat) => acc + cat.count, 0), percent: '100%' });

			return categoryCounts;
		},
		casesSectors() {
			if (this.allDecisions.length === 0) return [];

			let sectorCounts = this.allDecisions
				.filter((item) => item.isCase)
				.reduce((acc, item) => {
					item.sectors.forEach((sec) => {
						const secName = sec.name;
						const existingSector = acc.find((s) => s.name === secName);

						if (existingSector) {
							existingSector.count++;
						} else {
							acc.push({
								name: secName,
								count: 1,
								percent: '',
							});
						}
					});
					return acc;
				}, [] as Array<{ name: string; count: number; percent: string }>);

			const sectorCountsLength = sectorCounts.reduce((acc, cat) => acc + cat.count, 0);
			sectorCounts = sectorCounts
				.map((sec) => {
					let percent = Number(((sec.count / sectorCountsLength) * 100).toFixed(2));
					return {
						...sec,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			sectorCounts.push({ name: 'Summe (Entscheidungen)', count: sectorCountsLength, percent: '100%' });

			return sectorCounts;
		},
	},
	methods: {
		async doSearchDecisions() {
			const searchResult = await searchPublicCasesAndComplaints({}, this.tablePage, this.itemsPerPage);

			if (searchResult.success) {
				if (this.tablePage === 0) {
					this.data = searchResult.data.items;
				} else {
					this.data = [...this.data, ...searchResult.data.items];
				}

				this.totalCount = searchResult.data.totalCount;
			} else {
				this.data = [];
				this.totalCount = 0;
				this.errorFetchingData = true;
			}
		},
		async doSearchComplaints() {
			const searchResult = await searchPublicComplaints({}, this.tablePageComplaints, this.itemsPerPage);

			if (searchResult.success) {
				this.totalCountComplaints = searchResult.data.totalCount;
				if (this.tablePageComplaints === 0) {
					this.dataComplaints = searchResult.data.items;
				} else {
					this.dataComplaints = [...this.dataComplaints, ...searchResult.data.items];
				}
			} else {
				this.dataComplaints = [];
				this.totalCountComplaints = 0;
				this.errorFetchingComplaints = true;
			}
		},
		async loadAll() {
			this.loading = true;
			let cancel = false;

			// Timeout: in case doSearchDecisions() has an error and doesn't update the counts correctly
			const timeout = new Promise<void>((_, reject) => {
				setTimeout(() => {
					cancel = true;
					reject(new Error('Timeout reached after 20 seconds.'));
				}, 20000); // 20 seconds
			});

			// Loop for loading all decisions
			const loadDecisions = async () => {
				while (this.data.length < this.totalCount && !this.errorFetchingData) {
					if (cancel) return; // Stop if timeout occurs
					await this.doSearchDecisions();
					this.tablePage++;
				}
			};

			// Loop for loading all complaints
			const loadComplaints = async () => {
				while (this.dataComplaints.length < this.totalCountComplaints && !this.errorFetchingComplaints) {
					if (cancel) return; // Stop if timeout occurs
					await this.doSearchComplaints();
					this.tablePageComplaints++;
				}
			};

			const loading = startLoading('#statistik');
			try {
				// Load first batch of decisions and complaints
				await Promise.all([this.doSearchDecisions(), this.doSearchComplaints()]);
				this.tablePage++;
				this.tablePageComplaints++;

				// load rest (loops) and set Timeout
				await Promise.race([Promise.all([loadDecisions(), loadComplaints()]), timeout]);
			} catch (error) {
				console.error('Error during loading:', error);
			}

			this.loading = false;
			stopLoading(loading);
		},
	},
	mounted() {
		this.loadAll();
	},
});
</script>
